import { FC, useEffect, useMemo, useState } from "react";
import classes from './studentFilters.module.scss'
import { ToggleSwitch } from "../../../../shared/ui/toggleSwitch";
import { useSearchParams } from "react-router-dom";
import { adminService, IPayment } from "../../../../entities/admin";
import { DropDownListSelected } from "../../../../shared/ui/dropDownSelected";
import { IItem } from "../../../../shared/model/types";
import { WorkTgFilter } from "../../../workTgFilter";
import { OpenContainer } from "../../../../features/openContainer/ui/OpenContainer";
import { SurfaceBlock } from "../../../../shared/ui/surfaceBlock";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { FEATURES, hasFeature } from "../../../../entities/my";
import { useAppSelector } from "../../../../app/store/store";
import { LabelsFilter } from '../../../../shared/ui/labels';
import { IStudentTag, studentService } from '../../../../entities/student';

export const StudentFilters: FC = () => {

    const [params, setParams] = useSearchParams()

    const workTgIdsFromParams = useMemo(
        () => params.getAll('work_tg_ids').map(id => parseInt(id, 10)).filter(id => !isNaN(id)),
        [params]
    )
    const paymentIdsFromParams = useMemo(
        () => params.getAll('payment_ids').map(id => parseInt(id, 10)).filter(id => !isNaN(id)),
        [params]
    )
    const tagIdsFromParams = useMemo(
        () => params.getAll('tag_ids').map(id => parseInt(id, 10)).filter(id => !isNaN(id)),
        [params]
    )
    const isLostFromParams = params.get('is_lost') === 'true'
    const labelsFromParams = useMemo(() => params.getAll('labels'), [params])

    const [isLost, setIsLost] = useState<boolean>(isLostFromParams)
    const [workTgIds, setWorkTgIds] = useState<number[]>(workTgIdsFromParams)
    const [payments, setPayments] = useState<IPayment[]>([])
    const [tags, setTags] = useState<IStudentTag[]>([])
    const {setGlobalMessage} = useGlobalMessageActions()
    const {my} = useAppSelector(s => s.myReducer)
    const canListWorkTgs = hasFeature(my, FEATURES.WORK_TGS.LIST)
    const canListPayments = hasFeature(my, FEATURES.FINANCE.PAYMENTS_LIST)
    const [isLoadingPayments, setIsLoadingPayments] = useState<boolean>(canListPayments)
    const [isLoadingTags, setIsLoadingTags] = useState<boolean>(true)
    const [selectedPaymentIds, setSelectedPaymentIds] = useState<number[]>(paymentIdsFromParams)
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>(tagIdsFromParams)
    const [labels, setLabels] = useState<string[]>(labelsFromParams)
    const getPaymentId = (payment: IPayment) => payment.payment_id

    const getPaymentName = (payment: IPayment) => payment.payment_name

    const setFilterParams = (nextWorkTgIds: number[], nextIsLost: boolean, nextPaymentIds: number[], nextLabels: string[], nextTagIds: number[]) => {
        const newParams = new URLSearchParams(params)
        newParams.delete('work_tg_ids')
        if(nextWorkTgIds.length > 0){
            for(let id of nextWorkTgIds){
                newParams.append('work_tg_ids', String(id))
            }
        }
        if(nextIsLost){
            newParams.set('is_lost', 'true')
        }
        else{
            newParams.delete('is_lost')
        }
        newParams.delete('payment_ids')
        if(nextPaymentIds.length > 0){
            for(let id of nextPaymentIds){
                newParams.append('payment_ids', String(id))
            }
        }
        newParams.delete('labels')
        nextLabels.forEach(label => newParams.append('labels', label))
        newParams.delete('tag_ids')
        nextTagIds.forEach(id => newParams.append('tag_ids', String(id)))
        setParams(newParams)
    }

    useEffect(() => {
        setWorkTgIds(prev => {
            if(prev.length === workTgIdsFromParams.length && prev.every((id, index) => id === workTgIdsFromParams[index])){
                return prev
            }
            return workTgIdsFromParams
        })
        setSelectedPaymentIds(prev => {
            if(prev.length === paymentIdsFromParams.length && prev.every((id, index) => id === paymentIdsFromParams[index])){
                return prev
            }
            return paymentIdsFromParams
        })
        setSelectedTagIds(prev => {
            if(prev.length === tagIdsFromParams.length && prev.every((id, index) => id === tagIdsFromParams[index])){
                return prev
            }
            return tagIdsFromParams
        })
        setIsLost(isLostFromParams)
        setLabels(labelsFromParams)
    }, [workTgIdsFromParams, paymentIdsFromParams, tagIdsFromParams, isLostFromParams, labelsFromParams])

    const getPayments = async () => {
        try{
            setIsLoadingPayments(true)
            const res = await adminService.getPayments()
            setPayments(res)
        }
        catch(e){
            setPayments([])
            if(e instanceof AuthError && e.status === 403){
                setGlobalMessage({type: 'error', message: 'Нет прав на получение списка платежек'})
            }
            else{
                setGlobalMessage({type: 'error', message: 'Ошибка при получении списка платежек'})
            }
        }
        finally{
            setIsLoadingPayments(false)
        }
    }

    useEffect(() => {
        if(canListPayments){
            getPayments()
        }
        else{
            setPayments([])
            setIsLoadingPayments(false)
        }
    }, [canListPayments])

    useEffect(() => {
        const getTags = async () => {
            try{
                setIsLoadingTags(true)
                setTags(await studentService.getTags())
            }
            catch(e){
                setTags([])
                if(!(e instanceof AuthError && e.status === 403)){
                    setGlobalMessage({type: 'error', message: 'Ошибка при получении списка тегов'})
                }
            }
            finally{
                setIsLoadingTags(false)
            }
        }

        getTags()
    }, [])

    const onWorkTgIdsChange = (nextWorkTgIds: number[]) => {
        setWorkTgIds(nextWorkTgIds)
        setFilterParams(nextWorkTgIds, isLost, selectedPaymentIds, labels, selectedTagIds)
    }

    const onLostChange = (nextIsLost: boolean) => {
        setIsLost(nextIsLost)
        setFilterParams(workTgIds, nextIsLost, selectedPaymentIds, labels, selectedTagIds)
    }

    const onPaymentSelected = (item: IItem) => {
        return (selected: boolean) => {
            const normalizedItemId = Number(item.id)

            if(selected){
                const nextPaymentIds = selectedPaymentIds.includes(normalizedItemId)
                    ? selectedPaymentIds
                    : [...selectedPaymentIds, normalizedItemId]
                setSelectedPaymentIds(nextPaymentIds)
                setFilterParams(workTgIds, isLost, nextPaymentIds, labels, selectedTagIds)
            }
            else{
                const nextPaymentIds = selectedPaymentIds.filter(id => id !== normalizedItemId)
                setSelectedPaymentIds(nextPaymentIds)
                setFilterParams(workTgIds, isLost, nextPaymentIds, labels, selectedTagIds)
            }
        }
    }

    const onPaymentsSelectAll = (nextPaymentIds: number[]) => {
        setSelectedPaymentIds(nextPaymentIds)
        setFilterParams(workTgIds, isLost, nextPaymentIds, labels, selectedTagIds)
    }

    const onLabelsChange = (nextLabels: string[]) => {
        setLabels(nextLabels)
        setFilterParams(workTgIds, isLost, selectedPaymentIds, nextLabels, selectedTagIds)
    }

    const onTagSelected = (item: IItem) => {
        return (selected: boolean) => {
            const tagId = Number(item.id)
            const nextTagIds = selected
                ? (selectedTagIds.includes(tagId) ? selectedTagIds : [...selectedTagIds, tagId])
                : selectedTagIds.filter(id => id !== tagId)
            setSelectedTagIds(nextTagIds)
            setFilterParams(workTgIds, isLost, selectedPaymentIds, labels, nextTagIds)
        }
    }

    const onTagsSelectAll = (nextTagIds: number[]) => {
        setSelectedTagIds(nextTagIds)
        setFilterParams(workTgIds, isLost, selectedPaymentIds, labels, nextTagIds)
    }

    const paymentItems = useMemo(
        () => payments
            .map(payment => ({id: getPaymentId(payment), name: getPaymentName(payment)}))
            .filter(item => item.id > 0 && item.name.trim().length > 0),
        [payments]
    )
    const tagItems = useMemo(
        () => tags
            .map(tag => ({id: tag.id, name: tag.tag_name}))
            .filter(item => item.id > 0 && item.name.trim().length > 0),
        [tags]
    )

    return (
        <SurfaceBlock className={classes.container}>
            <OpenContainer title="Фильтры" allowOverflow>
                <section className={classes.content}>
                    {canListWorkTgs && <section className={classes.filterItem}>
                        <WorkTgFilter
                            setWorkTgIds={onWorkTgIdsChange}
                            initWorkTgIds={workTgIds}
                        />
                    </section>}
                    {
                        canListPayments && (isLoadingPayments || paymentItems.length > 0)
                            &&
                        <section className={`${classes.filterItem} ${classes.paymentsFilter}`}>
                            <span className={classes.label}>Платежки:</span>
                            <section className={classes.payments}>
                                <DropDownListSelected
                                    items={paymentItems}
                                    selectedIdItems={selectedPaymentIds}
                                    onSelected={onPaymentSelected}
                                    onSelectAll={onPaymentsSelectAll}
                                    selectedCount
                                    isLoading={isLoadingPayments}
                                    placeholder="Выберите платежки"
                                    search
                                    searchPlaceholder="Поиск по платежкам"
                                    emptyText="Платежки не найдены"
                                />
                            </section>
                        </section>
                    }
                    {(isLoadingTags || tagItems.length > 0) && <section className={`${classes.filterItem} ${classes.paymentsFilter}`}>
                        <span className={classes.label}>Теги:</span>
                        <section className={classes.payments}>
                            <DropDownListSelected
                                items={tagItems}
                                selectedIdItems={selectedTagIds}
                                onSelected={onTagSelected}
                                onSelectAll={onTagsSelectAll}
                                selectedCount
                                isLoading={isLoadingTags}
                                placeholder="Выберите теги"
                                search
                                searchPlaceholder="Поиск по тегам"
                                emptyText="Теги не найдены"
                            />
                        </section>
                    </section>}
                    <section className={classes.filterItem}>
                        <section className={classes.toggle}>
                            <span className={classes.toggleLabel}>Должники:</span>
                            <ToggleSwitch
                                checked={isLost}
                                onSelected={onLostChange}
                            />
                        </section>
                    </section>
                    <section className={classes.filterItem}>
                        <LabelsFilter value={labels} onChange={onLabelsChange} loadOptions={studentService.getLabels.bind(studentService)} />
                    </section>
                </section>
            </OpenContainer>
        </SurfaceBlock>
    )
}
